import {html, type HTMLTemplateResult, type PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {get, translate} from "lit-translate";

import {AbstractO2dElement} from "@o2d/shared";
import {formatTime, parts} from "../../../domain/gimmicks/stopwatch.state";
import {
    adjust,
    canDecrease,
    canIncrease,
    finish,
    follow,
    isDone,
    isRunning,
    pause,
    remaining,
    reset,
    start,
    TIMER_DEFAULT_MINUTES,
    TIMER_STEP_MS,
    type TimerState,
} from "../../../domain/gimmicks/timer.state";
import {loadMuted, loadState, saveMuted, saveState, subscribe, timerKeyOf} from "../../../domain/gimmicks/timer.store";
import {changed, check, prime, watchTimer} from "../../../services/timer-watch";
import {FrameLoopController} from "../frame-loop.controller";
import {GimmickSkinController} from "../gimmick-skin.controller";
import {CLOCK_SKIN_RENDERERS} from "../stopwatch/skins/clock-skins";
import type {SkinInput} from "../stopwatch/skins/skin-input";

/** A "Starten" this soon after the element saw the run end is ignored — the painted button still said "Anhalten". */
export const START_LOCK_AFTER_END_MS = 500;

/** The time left as shown: rounded UP to hundredths, so `00:00,00` appears only at the very end. */
export function shownMs(left: number): number {
    return Math.ceil(left / 10) * 10;
}

/**
 * The timer gimmick (feature plan 0073), mounted by `link-tile-gimmick` inside a Linkboard tile: counts a preset
 * of whole minutes down, with the four clock looks of the stopwatch, ± one minute, blinking and one short tone at
 * the end, the tone switchable per viewer.
 *
 * - STATE per viewer and tile in browser storage (`timer.store.ts`), like the stopwatch. LOADING NEVER WRITES:
 *   connecting, a new tile id or preset, another tab's write only read and apply `follow` (a changed preset
 *   replaces an unstarted timer) in this element's memory. Only clicks write — and the app-wide watcher at the
 *   end (`services/timer-watch.ts`), which also beeps in other views of the app.
 * - END: the watcher reports it (`watchTimer`); `data-done` follows `isDone`, so a timer loaded after its end
 *   blinks too. When a frame or the page becoming visible shows nothing left, the element asks the watcher
 *   (`check`); if that ends nothing and storage still says "running, time up" (storage blocked or full), the
 *   element ends the run in its own memory only — the frame loop stops instead of asking every frame.
 * - CLICKS: what a click means is fixed from the shown state before the watcher looks; if the watcher ends the
 *   run then, start/pause and ± do nothing more. A "Starten" within 500 ms after the element saw the run end is
 *   ignored. "Starten" and every click on a running timer unlock the tone (`prime`). The sound switch stores
 *   first and only then lets the watcher look — muting in the last second means silence.
 * - A11Y: `role="timer"`, the readable time left in the `aria-label` (rounded up like the display), renewed on
 *   start, pause, reset, effective ±, the end and every load — never per frame; "Timer: Zeit abgelaufen." after
 *   the end. A live region always rendered gets "Zeit abgelaufen." when this tab saw the end live (also muted),
 *   never on load or from another tab; start, reset and an effective ± clear it. The sound button has a fixed
 *   name and `aria-pressed`.
 *
 * A NEW component, so it extends `AbstractO2dElement` (ADR-007; Light DOM); styles in ralphy.css, sections
 * "Gimmicks: gemeinsamer Rahmen" and "Gimmicks: Timer"; the looks use the stopwatch's `stopwatch-*` classes.
 */
@customElement('o2d-gimmick-timer')
export class GimmickTimerComponent extends AbstractO2dElement {

    /** The tile's id — the key of this viewer's state. */
    @property({type: String})
    public tileId = '';

    /** The look stored with the tile, raw. */
    @property({type: String})
    public savedSkin = '';

    /** The preset stored with the tile, in milliseconds (`presetMs(options)`). */
    @property({type: Number})
    public presetMs = TIMER_DEFAULT_MINUTES * TIMER_STEP_MS;

    @state()
    private _state: TimerState = reset(TIMER_DEFAULT_MINUTES * TIMER_STEP_MS);

    /** "Now" of the last frame — only moves while running. */
    @state()
    private _now = Date.now();

    /** What the `aria-label` reads — renewed by `_announce`, never per frame. */
    @state()
    private _label = '';

    /** The live region's text. */
    @state()
    private _live = '';

    @state()
    private _muted = false;

    /** When this element saw a run change from running to ended (for the start lock). */
    private _endSeenAt: number | null = null;
    /** Whether the element's last write worked. */
    private _lastSaveOk = true;
    private _unsubscribeStore: (() => void) | null = null;
    private _unsubscribeWatch: (() => void) | null = null;

    private readonly _skinController = new GimmickSkinController(this, 'timer');
    private readonly _frameLoop = new FrameLoopController(this, {
        running: () => isRunning(this._state),
        onFrame: () => {
            this._now = Date.now();
            if (remaining(this._state, this._now) === 0) {
                this._onTimeUp();
            }
        },
        onVisible: () => {
            if (isRunning(this._state) && remaining(this._state, Date.now()) === 0) {
                this._onTimeUp();
            }
        },
        schedule: (callback) => this.requestFrame(callback),
    });

    public override connectedCallback(): void {
        super.connectedCallback();
        this._listen();
        this._reload();
    }

    public override disconnectedCallback(): void {
        this._unlisten();
        super.disconnectedCallback(); // the frame loop stops; the base cancels the pending frame
    }

    protected override willUpdate(changedProperties: PropertyValues): void {
        // The first update follows `connectedCallback`, which already listened and loaded.
        if (!this.hasUpdated || !this.isConnected) {
            return;
        }
        if (changedProperties.has('tileId')) {
            this._listen();
            this._reload();
        } else if (changedProperties.has('presetMs')) {
            this._reload();
        }
    }

    protected render(): HTMLTemplateResult {
        const now = this._now;
        const running = isRunning(this._state);
        const done = isDone(this._state, now);
        const left = shownMs(remaining(this._state, now));
        const skin = this._skinController.skin;
        const input: SkinInput = {
            ms: left,
            parts: parts(left),
            running,
            fraction: this._state.durationMs > 0 ? remaining(this._state, now) / this._state.durationMs : 0,
        };
        const showPause = running && !done;

        return html`
            <div class="gimmick timer" data-skin=${skin} data-running=${running ? 'true' : 'false'}
                 data-done=${done ? 'true' : 'false'} role="timer" aria-label=${this._label}>
                <div class="gimmick__face">${CLOCK_SKIN_RENDERERS[skin](input)}</div>
                <div class="gimmick__controls">
                    <button type="button"
                            class=${showPause ? 'gimmick__toggle gimmick__toggle--pause' : 'gimmick__toggle gimmick__toggle--start'}
                            aria-label=${translate(showPause ? 'gimmicks.pause' : 'gimmicks.start')}
                            title=${translate(showPause ? 'gimmicks.pause' : 'gimmicks.start')}
                            @click=${this._onToggle}></button>
                    <button type="button" class="gimmick__reset"
                            aria-label=${translate('gimmicks.reset')}
                            title=${translate('gimmicks.reset')}
                            @click=${this._onReset}></button>
                    <button type="button" class="timer__minus"
                            aria-label=${translate('gimmicks.timer.minus')}
                            title=${translate('gimmicks.timer.minus')}
                            aria-disabled=${canDecrease(this._state, now) ? 'false' : 'true'}
                            @click=${this._onMinus}></button>
                    <button type="button" class="timer__plus"
                            aria-label=${translate('gimmicks.timer.plus')}
                            title=${translate('gimmicks.timer.plus')}
                            aria-disabled=${canIncrease(this._state, now) ? 'false' : 'true'}
                            @click=${this._onPlus}></button>
                    <button type="button" class="timer__mute"
                            aria-label=${translate('gimmicks.timer.mute')}
                            title=${translate('gimmicks.timer.mute')}
                            aria-pressed=${this._muted ? 'true' : 'false'}
                            @click=${this._onMute}></button>
                    <button type="button" class="gimmick__next-skin"
                            aria-label=${translate('gimmicks.next-skin')}
                            title=${translate('gimmicks.next-skin')}
                            @click=${this._onNextSkin}></button>
                </div>
                <div class="gimmick__announce" aria-live="polite">${this._live}</div>
            </div>
        `;
    }

    private get _key(): string {
        return timerKeyOf(this.tileId);
    }

    private readonly _onToggle = (): void => {
        const now = Date.now();
        const wantsPause = isRunning(this._state) && !isDone(this._state, now);
        if (!wantsPause && this._endSeenAt !== null && now - this._endSeenAt < START_LOCK_AFTER_END_MS) {
            return;
        }
        prime();
        if (check(this._key)) {
            return;
        }
        if (wantsPause) {
            this._apply(pause(this._state, now), now);
        } else {
            this._endSeenAt = null;
            this._live = '';
            this._apply(start(this._state, now, this.presetMs), now);
        }
    };

    private readonly _onReset = (): void => {
        if (isRunning(this._state)) {
            prime();
        }
        check(this._key);
        this._endSeenAt = null;
        this._live = '';
        this._apply(reset(this.presetMs), Date.now());
    };

    private readonly _onMinus = (): void => {
        this._onAdjust(-1);
    };

    private readonly _onPlus = (): void => {
        this._onAdjust(1);
    };

    private _onAdjust(direction: -1 | 1): void {
        const now = Date.now();
        if (isRunning(this._state)) {
            prime();
            if (check(this._key)) {
                return;
            }
        }
        if (direction < 0 ? !canDecrease(this._state, now) : !canIncrease(this._state, now)) {
            return;
        }
        this._live = '';
        this._apply(adjust(this._state, direction, now), now);
    }

    private readonly _onMute = (): void => {
        this._muted = !this._muted;
        saveMuted(this.tileId, this._muted);
        if (isRunning(this._state)) {
            prime();
            check(this._key);
        }
    };

    private readonly _onNextSkin = (): void => {
        this._skinController.next();
    };

    private _apply(next: TimerState, now: number): void {
        this._state = next;
        this._lastSaveOk = saveState(this.tileId, next);
        changed(this._key);
        this._announce(now);
        this._frameLoop.sync();
    }

    /** A frame or the page becoming visible shows nothing left: let the watcher end the run; fall back to ending
     *  it in memory when storage cannot hold the end. */
    private _onTimeUp(): void {
        if (check(this._key)) {
            return;
        }
        const now = Date.now();
        if (this._lastSaveOk) {
            this._reload();
        }
        if (isRunning(this._state) && remaining(this._state, now) === 0) {
            this._state = finish(this._state);
            this._endSeenAt = now;
            this._announce(now);
            this._frameLoop.sync();
        }
    }

    /** The watcher ended this tile's run. */
    private readonly _onWatchDone = (silent: boolean): void => {
        this._reload();
        if (!silent) {
            this._live = get('gimmicks.timer.done');
        }
    };

    /** Renews "now" and what the `aria-label` reads. */
    private _announce(now: number): void {
        this._now = now;
        this._label = isDone(this._state, now)
            ? get('gimmicks.timer.done-label')
            : get('gimmicks.timer.timer-label', {time: formatTime(parts(shownMs(remaining(this._state, now))))});
    }

    /** Reads state and sound switch — never writes. */
    private _reload(): void {
        const now = Date.now();
        const before = this._state;
        const loaded = loadState(this.tileId, now) ?? reset(this.presetMs);
        this._state = follow(loaded, this.presetMs);
        this._muted = loadMuted(this.tileId);
        // The change "ran → ended" in this element, whether reported by the watcher or by another tab's write.
        if (isRunning(before) && !isRunning(this._state) && isDone(this._state, now)) {
            this._endSeenAt = now;
        }
        this._announce(now);
        this._frameLoop.sync();
    }

    private _listen(): void {
        this._unlisten();
        if (!this.tileId) {
            return;
        }
        this._unsubscribeStore = subscribe(this.tileId, () => this._reload());
        this._unsubscribeWatch = watchTimer(this._key, this._onWatchDone);
    }

    private _unlisten(): void {
        this._unsubscribeStore?.();
        this._unsubscribeStore = null;
        this._unsubscribeWatch?.();
        this._unsubscribeWatch = null;
    }
}
